import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Alert, Button, Table, Tag } from 'antd';
import moment from 'moment';
import { useMaiaT } from '$app/i18n/useMaiaT';
import { getStoredSelectedSegment, SEGMENT_SELECTION_CHANGED_EVENT } from '$app/segmentSelection';
import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import { listImportJobs } from './actions';
import { importSegment, usePolling } from './importHelpers';

export function GarageImportListPage({ history }) {
  const t = useMaiaT();
  const [segment, setSegment] = useState(() => importSegment(getStoredSelectedSegment()));
  const [jobs, setJobs] = useState([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const handleSegmentChange = () => setSegment(importSegment(getStoredSelectedSegment()));
    window.addEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
    return () => window.removeEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
  }, []);

  const load = useCallback(async () => {
    if (!segment) return;
    try {
      setJobs(await listImportJobs(segment));
      setLoadError(false);
    } catch (error) {
      console.error('GarageImportListPage load error', { segment, error });
      setLoadError(true);
    }
  }, [segment]);

  useEffect(() => {
    load();
  }, [load]);

  usePolling(
    load,
    jobs.some((job) => job.status === 'IMPORTING')
  );

  if (!segment) {
    return (
      <Container>
        <Alert
          type="info"
          showIcon
          message={t(
            'maia.garageImport.unavailable',
            'Garage imports are available for Motrio and Renault segments.'
          )}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('maia.garageImport.listTitle', 'Garage imports')}</Title>
        <div data-testid="import-segment">{segment}</div>
        <Button type="primary" style={{ marginRight: 8 }} onClick={() => history.push('/imports/new/create')}>
          {t('maia.garageImport.mode.create', 'Create garages')}
        </Button>
        <Button onClick={() => history.push('/imports/new/update')}>
          {t('maia.garageImport.mode.update', 'Update garages')}
        </Button>
      </Header>
      {loadError && (
        <Alert
          type="error"
          showIcon
          style={{ margin: 16 }}
          message={t('maia.garageImport.loadError', 'The import could not be loaded.')}
        />
      )}
      <Table
        size="small"
        rowKey="jobId"
        dataSource={jobs}
        onRow={(job) => ({ onClick: () => history.push(`/imports/${job.jobId}`) })}
        columns={[
          {
            title: t('maia.garageImport.column.uploadedAt', 'Uploaded'),
            dataIndex: 'createdAt',
            render: (value) => moment(value).format('D MMM YYYY, HH:mm'),
          },
          { title: t('maia.garageImport.column.file', 'File'), dataIndex: 'fileName' },
          { title: t('maia.garageImport.column.mode', 'Mode'), dataIndex: 'mode' },
          { title: t('maia.garageImport.column.author', 'Uploaded by'), dataIndex: 'author' },
          {
            title: t('maia.garageImport.column.status', 'Status'),
            dataIndex: 'status',
            render: (status) => <Tag>{status}</Tag>,
          },
          {
            title: t('maia.garageImport.column.progress', 'Progress'),
            key: 'progress',
            render: (_, { progress }) => `${progress.done}/${progress.total - progress.invalid}`,
          },
        ]}
      />
    </Container>
  );
}

export default withRouter(GarageImportListPage);
